import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, DoorOpen, Fuel, Settings2, Snowflake, Star, ShieldCheck,
  Calendar, ArrowLeft, MapPin, Wallet, FileText, Loader2,
} from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { VehicleGallery } from "@/components/VehicleGallery";
import { WhatsAppContact } from "@/components/WhatsAppContact";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

export default function CarDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [vehicle, setVehicle] = useState(null);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ client_name: "", client_email: "", client_phone: "", start_date: sp.get("start") || "", end_date: sp.get("end") || "", message: "" });

  useEffect(() => {
    api.get(`/vehicles/${id}`).then((r) => setVehicle(r.data)).catch(() => toast.error("Véhicule introuvable"));
  }, [id]);

  const days = (() => {
    if (!form.start_date || !form.end_date) return 0;
    const d = (new Date(form.end_date) - new Date(form.start_date)) / 86400000;
    return d > 0 ? Math.ceil(d) : 0;
  })();
  const total = days * (vehicle?.price_per_day || 0);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/rental-requests", { vehicle_id: id, ...form });
      toast.success("Demande envoyée ! Le loueur vous recontactera rapidement.");
      setOpen(false);
      setForm({ client_name: "", client_email: "", client_phone: "", start_date: "", end_date: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!vehicle) {
    return <PublicLayout><div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div></PublicLayout>;
  }

  const specs = [
    { icon: Users, label: "Places", value: vehicle.seats },
    { icon: DoorOpen, label: "Portes", value: vehicle.doors },
    { icon: Settings2, label: "Boîte", value: vehicle.transmission },
    { icon: Fuel, label: "Carburant", value: vehicle.fuel },
    { icon: Snowflake, label: "Climatisation", value: vehicle.air_conditioning ? "Oui" : "Non" },
    { icon: Calendar, label: "Année", value: vehicle.year },
  ];

  return (
    <PublicLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="car-back-btn">
          <ArrowLeft className="h-4 w-4" /> Retour
        </button>

        <div className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            <VehicleGallery vehicle={vehicle} />

            <div className="mt-8">
              <h3 className="font-display text-xl font-bold text-slate-900 mb-4">Caractéristiques</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {specs.map((s) => (
                  <div key={s.label} className="rounded-xl border border-slate-200 bg-white p-4">
                    <s.icon className="h-5 w-5 text-slate-400 mb-2" />
                    <p className="text-xs text-slate-500">{s.label}</p>
                    <p className="font-semibold text-slate-900">{s.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {vehicle.options && (
              <div className="mt-8">
                <h3 className="font-display text-xl font-bold text-slate-900 mb-3">Options incluses</h3>
                <div className="flex flex-wrap gap-2">
                  {vehicle.options.split(",").map((o, i) => (
                    <span key={i} className="rounded-full bg-slate-100 px-3 py-1.5 text-sm text-slate-700">{o.trim()}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6">
              <h3 className="flex items-center gap-2 font-display text-xl font-bold text-slate-900 mb-4">
                <FileText className="h-5 w-5" style={{ color: "#C1272D" }} /> Conditions de location
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <span className="flex items-center gap-2 text-slate-600"><Wallet className="h-4 w-4" /> Caution</span>
                  <span className="font-semibold text-slate-900">{vehicle.deposit?.toLocaleString()} MAD</span>
                </div>
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <span className="flex items-center gap-2 text-slate-600"><MapPin className="h-4 w-4" /> Kilométrage</span>
                  <span className="font-semibold text-slate-900">{vehicle.mileage_policy}</span>
                </div>
                {vehicle.conditions && <p className="text-slate-600 pt-1">{vehicle.conditions}</p>}
              </div>
            </div>
          </div>

          {/* SIDEBAR */}
          <div className="lg:col-span-2">
            <div className="sticky top-20 space-y-5">
              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <span className="inline-block rounded-full bg-black/80 px-2.5 py-1 text-[11px] font-semibold text-white">{vehicle.category}</span>
                <h1 className="mt-3 font-display text-3xl font-extrabold text-slate-900">{vehicle.brand} {vehicle.model}</h1>
                <p className="text-slate-500 mt-1 flex items-center gap-1.5"><MapPin className="h-4 w-4" /> {vehicle.city} · {vehicle.year}</p>

                <div className="mt-5 flex items-baseline gap-1">
                  <span className="font-display text-4xl font-extrabold text-slate-900">{vehicle.price_per_day}</span>
                  <span className="text-slate-500 font-medium">MAD / jour</span>
                </div>

                <Dialog open={open} onOpenChange={setOpen}>
                  <DialogTrigger asChild>
                    <Button className="w-full mt-5 h-12 text-base font-semibold text-white" style={{ background: "#006233" }} data-testid="open-rental-request-btn">
                      Envoyer une demande
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle>Demande de location</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={submit} className="space-y-3">
                      <input required placeholder="Nom complet" data-testid="req-name" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} className="h-11 w-full rounded-md border border-input px-3 text-sm" />
                      <input required type="email" placeholder="Email" data-testid="req-email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} className="h-11 w-full rounded-md border border-input px-3 text-sm" />
                      <input required placeholder="Téléphone" data-testid="req-phone" value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} className="h-11 w-full rounded-md border border-input px-3 text-sm" />
                      <div className="grid grid-cols-2 gap-3">
                        <input required type="date" data-testid="req-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="h-11 w-full rounded-md border border-input px-3 text-sm" />
                        <input required type="date" data-testid="req-end" min={form.start_date || undefined} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="h-11 w-full rounded-md border border-input px-3 text-sm" />
                      </div>
                      <textarea placeholder="Message (optionnel)" data-testid="req-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full rounded-md border border-input px-3 py-2 text-sm" rows={3} />
                      {days > 0 && (
                        <div className="rounded-lg bg-slate-50 p-3 flex items-center justify-between text-sm">
                          <span className="text-slate-600">{days} jour(s) × {vehicle.price_per_day} MAD</span>
                          <span className="font-bold text-slate-900">{total.toLocaleString()} MAD</span>
                        </div>
                      )}
                      <DialogFooter>
                        <Button type="submit" disabled={submitting} className="w-full text-white" style={{ background: "#006233" }} data-testid="rental-request-form-submit">
                          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmer ma demande"}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>

                <WhatsAppContact vehicle={vehicle} dates={{ start: form.start_date, end: form.end_date }} variant="button" />
                <p className="text-xs text-center text-slate-400 mt-3">Sans commission cachée · Réponse directe du propriétaire</p>
              </div>

              {vehicle.agency && (
                <div className="rounded-3xl border border-slate-200 bg-white p-6" data-testid="car-agency-box">
                  <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-3">Propriétaire</p>
                  <div className="flex items-center gap-3">
                    <WhatsAppContact vehicle={vehicle} dates={{ start: form.start_date, end: form.end_date }} />
                    <div>
                      <p className="font-bold text-slate-900">Loueur vérifié</p>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" /> {vehicle.agency.rating} · {vehicle.agency.city}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-1.5 text-sm" style={{ color: "#006233" }}>
                    <ShieldCheck className="h-4 w-4" /> Identité et véhicules contrôlés par link&rent
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
